import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { PriceName } from 'src/app/models/enum/price-name';
import { Price } from 'src/app/models/product/price';
import { PriceDto } from 'src/app/models/product/price-dto';
import { PriceEdit } from 'src/app/models/product/price-edit';
import { PriceSearchCriteria } from 'src/app/models/product/price-search-criteria';
import { environment } from 'src/environments/environment';


const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json'
  })
};

@Injectable({
  providedIn: 'root'
})
export class PriceService {

  private apiUrl = environment.apiUrl;
  
  /**
   * Subject that emits when a price is successfully updated.
   * Consumers (e.g., view-plans) can subscribe to automatically update their displayed data.
   * Emits the updated PriceDto so consumers can refresh their list.
   */
  private priceUpdated$ = new Subject<{ id: number; updatedPrice: PriceDto }>();

  /**
   * Tracks the ID of the most recently edited price.
   * view-plans uses this to detect when to refresh the search results.
   */
  private lastEditedPriceId: number | null = null;

  /**
   * Caches the last search criteria used in view-plans.
   * When user returns from editing, we re-apply the same search to get fresh data.
   */
  private lastSearchCriteria: PriceSearchCriteria | null = null;

  constructor(
    private httpClient: HttpClient, 
  ) {
  }
  
  /**
   * Observable that emits when a price is successfully updated.
   * Subscribe to this to react to price updates across the application.
   */
  getPriceUpdatedNotifier(): Observable<{ id: number; updatedPrice: PriceDto }> {
    return this.priceUpdated$.asObservable();
  }

  /**
   * Returns the ID of the most recently edited price (if any).
   * Clears the value after retrieval (one-time check).
   */
  getAndClearLastEditedPriceId(): number | null {
    const id = this.lastEditedPriceId;
    this.lastEditedPriceId = null;
    return id;
  }

  /**
   * Saves the search criteria used in view-plans.
   * This allows automatic re-search when user returns from editing.
   */
  cacheSearchCriteria(criteria: PriceSearchCriteria): void {
    this.lastSearchCriteria = criteria ? { ...criteria } : null;
  }

  /**
   * Returns the cached search criteria (if any).
   */
  getCachedSearchCriteria(): PriceSearchCriteria | null {
    return this.lastSearchCriteria;
  }
  
  createNewPrice(price: Price): Observable<Price> {
    return this.httpClient.post<Price>(`${this.apiUrl}/api/v1/price`, price, httpOptions);
  }

  updatePrice(price: PriceEdit, id: number): Observable<Price> {
    return this.httpClient.put<Price>(`${this.apiUrl}/api/v1/price/${id}`, price, httpOptions)
      .pipe(
        tap((_updatedPrice) => {
          // Track that this price was just edited
          this.lastEditedPriceId = id;
          
          // After successful update, emit notification so other components can update their UI
          // Re-fetch the full DTO to ensure all calculated fields are included
          this.getByPriceId(id).subscribe(fullPrice => {
            this.priceUpdated$.next({ id, updatedPrice: fullPrice });
          });
        })
      );
  }
  
  searchPrice(priceGet: PriceSearchCriteria): Observable<PriceDto[]> {
    return this.httpClient.post<PriceDto[]>(`${this.apiUrl}/api/v1/price/search-criteria`, priceGet, httpOptions)
      .pipe(
        map((response: PriceDto[]) => {
          return response;
        })
      );
  }
 


    getByCustomersPrices(priceNames: PriceName[]): Observable<PriceDto[]> {
        let params = new HttpParams();
        priceNames.forEach(n => params = params.append('name', n));

        return this.httpClient.get<PriceDto[]>(
            `${this.apiUrl}/api/v1/price/price-name`,
            { params }
        );
    }

  getByPriceId(id: number): Observable<PriceDto> {
    return this.httpClient.get<PriceDto>(`${this.apiUrl}/api/v1/price/price-id/${id}`)
      .pipe(
        map((response: PriceDto) => {
          return response;
        })
      );
  }  
  
}
